// ABOUTME: Custom hook encapsulating all Navigation state, refs, callbacks, and side-effects
// ABOUTME: Keeps the main Navigation component a thin render shell
import { useCallback, useEffect, useRef, useState } from "react"

import type { NavigationTabsProps } from "./types"
import { useClickOutside } from "./useClickOutside"
import { useMenuItems } from "./useMenuItems"
import { useTabOverflow } from "./useTabOverflow"

export const useNavigationState = (
	navigationTabs: NavigationTabsProps[] | undefined,
) => {
	const [isMenuOpened, setIsMenuOpened] = useState(false)
	const [isMoreMenuOpened, setIsMoreMenuOpened] = useState(false)
	const [openDropdown, setOpenDropdown] = useState<string | null>(null)

	// --- Refs ---

	const menuButtonRef = useRef<HTMLButtonElement>(null)
	const menuRef = useRef<HTMLDivElement>(null)
	const moreButtonRef = useRef<HTMLButtonElement>(null)
	const moreMenuRef = useRef<HTMLDivElement>(null)
	const desktopContainerRef = useRef<HTMLDivElement>(null)
	const tabsPanelRef = useRef<HTMLDivElement>(null)
	const leftButtonsRef = useRef<HTMLDivElement>(null)
	const rightButtonsRef = useRef<HTMLDivElement>(null)
	const logoRef = useRef<HTMLDivElement>(null)
	const tabDropdownRefs = useRef<{ [key: string]: HTMLDivElement | null }>({})

	// --- Overflow & menu items ---

	const { visibleTabs, overflowTabs, shouldShowMore } = useTabOverflow({
		navigationTabs,
		containerRef: desktopContainerRef,
		tabsPanelRef,
		leftButtonsRef,
		rightButtonsRef,
		logoRef,
	})

	const { menuItems, overflowMenuItems, convertSubroutesToMenuItems } =
		useMenuItems(navigationTabs, overflowTabs)

	// --- Callbacks ---

	const closeAllDropdowns = useCallback(() => {
		setOpenDropdown(null)
	}, [])

	const toggleDropdown = useCallback((label: string) => {
		setIsMoreMenuOpened(false)
		setOpenDropdown(prev => (prev === label ? null : label))
	}, [])

	const toggleMoreMenu = useCallback(() => {
		closeAllDropdowns()
		setIsMoreMenuOpened(prev => !prev)
	}, [closeAllDropdowns])

	const toggleMobileMenu = useCallback(() => {
		setIsMenuOpened(prev => !prev)
	}, [])

	const closeMobileMenu = useCallback(() => {
		setIsMenuOpened(false)
	}, [])

	// --- Click-outside handlers ---

	useClickOutside({
		refs: [menuRef, menuButtonRef],
		isOpen: isMenuOpened,
		onClose: closeMobileMenu,
	})

	useClickOutside({
		refs: [moreMenuRef, moreButtonRef],
		isOpen: isMoreMenuOpened,
		onClose: () => setIsMoreMenuOpened(false),
	})

	const dropdownContainerRef = useRef<HTMLDivElement | null>(null)

	useEffect(() => {
		dropdownContainerRef.current = openDropdown
			? tabDropdownRefs.current[openDropdown] ?? null
			: null
	}, [openDropdown])

	useClickOutside({
		refs: [dropdownContainerRef],
		isOpen: openDropdown !== null,
		onClose: closeAllDropdowns,
	})

	// --- Side-effects ---

	useEffect(() => {
		document.body.style.overflow = isMenuOpened ? "hidden" : "unset"
		return () => {
			document.body.style.overflow = "unset"
		}
	}, [isMenuOpened])

	useEffect(() => {
		if (!isMenuOpened) return

		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				setIsMenuOpened(false)
			}
		}

		document.addEventListener("keydown", handleEscape)
		return () => document.removeEventListener("keydown", handleEscape)
	}, [isMenuOpened])

	return {
		// State
		isMenuOpened,
		isMoreMenuOpened,
		openDropdown,
		// Overflow
		visibleTabs,
		overflowTabs,
		shouldShowMore,
		// Menu items
		menuItems,
		overflowMenuItems,
		convertSubroutesToMenuItems,
		// Callbacks
		closeAllDropdowns,
		toggleDropdown,
		toggleMoreMenu,
		toggleMobileMenu,
		closeMobileMenu,
		// Refs
		menuButtonRef,
		menuRef,
		moreButtonRef,
		moreMenuRef,
		desktopContainerRef,
		tabsPanelRef,
		leftButtonsRef,
		rightButtonsRef,
		logoRef,
		tabDropdownRefs,
	}
}
